import { EventTypeEnum } from '../../types/event';
import {
  DEFAULT_FILE_VISIBILITY,
  FILE_VISIBILITY_OPTIONS,
  type FileVisibility,
} from '../../types/fileVisibility';

export const FOLDER_UPLOAD_TYPE = '__folder__';

export const supportedFileTypes = [
  {
    title: 'Any File',
    const: '*',
  },
  {
    title: 'Audio',
    const: 'audio/*',
  },
  {
    title: 'CSV',
    const: '.csv',
  },
  {
    title: 'Documents',
    const: 'application/*',
  },
  {
    title: 'Excel 2007+',
    const: '.xlsx',
  },
  {
    title: 'Excel 97-2003',
    const: '.xls',
  },
  {
    title: 'Excel Binary',
    const: '.xlsb',
  },
  {
    title: 'Folder',
    const: FOLDER_UPLOAD_TYPE,
  },
  {
    title: 'ICO',
    const: '.ico',
  },
  {
    title: 'Images',
    const: 'image/*',
  },
  {
    title: 'JPEG',
    const: 'image/jpeg',
  },
  {
    title: 'JPG',
    const: 'image/jpg',
  },
  {
    title: 'JSON (File Extension)',
    const: '.json',
  },
  {
    title: 'Lottie',
    const: '.lottie',
  },
  {
    title: 'JSON (MIME Type)',
    const: 'application/json',
  },
  {
    title: 'Markdown',
    const: 'text/markdown',
  },
  {
    title: 'Outlook Message',
    const: '.msg',
  },
  {
    title: 'PDF',
    const: '.pdf',
  },
  {
    title: 'Plain text',
    const: 'text/plain',
  },
  {
    title: 'PNG',
    const: '.png',
  },
  {
    title: 'PowerPoint (Open XML)',
    const: '.pptx',
  },
  {
    title: 'PowerPoint',
    const: '.ppt',
  },
  {
    title: 'SVG',
    const: '.svg',
  },
  {
    title: 'Text',
    const: 'text/*',
  },
  {
    title: 'Videos',
    const: 'video/*',
  },
  {
    title: 'WebP',
    const: 'image/webp',
  },
  {
    title: 'Word Document',
    const: '.doc, .docx',
  },
  {
    title: 'XML',
    const: '.xml',
  },
  {
    title: 'XSD',
    const: '.xsd',
  },
  {
    title: 'ZIP',
    const: '.zip',
  },
];

export const getContentSchema = (
  defaultFileVisibility: FileVisibility = DEFAULT_FILE_VISIBILITY,
) => {
  return {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    properties: {
      label: {
        type: 'string',
      },
      divider: {
        type: 'boolean',
      },
      fileRestrictions: {
        type: 'object',
        properties: {
          selectionVariant: {
            type: 'string',
            oneOf: [
              {
                title: 'Single',
                const: 'single',
              },
              {
                title: 'Multiple',
                const: 'multiple',
              },
            ],
          },
          supportedFileTypes: {
            type: 'array',
            items: {
              type: 'string',
              oneOf: supportedFileTypes,
            },
          },
          maxFileSize: {
            type: 'number',
          },
        },
        allOf: [
          {
            if: {
              properties: {
                selectionVariant: {
                  const: 'multiple',
                },
              },
            },
            then: {
              properties: {
                maxFiles: {
                  type: 'number',
                },
              },
            },
          },
        ],
      },
      accessOptions: {
        type: 'object',
        properties: {
          fileVisibility: {
            type: 'string',
            oneOf: FILE_VISIBILITY_OPTIONS,
            default: defaultFileVisibility,
          },
          publicAccessWarning: {
            type: 'string',
          },
          allowDownload: {
            type: 'boolean',
            default: true,
          },
        },
      },
      addOns: {
        type: 'object',
        properties: {
          templateFile: {
            type: 'object',
          },
          description: {
            type: 'string',
          },
        },
      },
      divider1: {
        type: 'boolean',
      },
      interactions: {
        type: 'string',
      },
      divider2: {
        type: 'string',
      },
      permissions: {
        type: 'string',
      },
    },
  };
};

export function getContentUiSchema({
  isFileVisibilityPublic,
}: {
  isFileVisibilityPublic?: boolean;
}) {
  return {
    label: {
      'ui:title': 'Label',
      'ua:i18n': true,
    },
    fileRestrictions: {
      'ui:title': 'File Restrictions',
      'ui:ObjectFieldTemplate': 'InterfaceSectionTemplate',
      'ua:options': {
        disableBackground: true,
        interfaceSection: {
          showTopDivider: true,
          initiallyExpand: true,
        },
      },
      supportedFileTypes: {
        'ui:widget': 'MultiSelectWidget',
        'ui:title': 'Supported File Types',
        'ui:description': 'Select the file types that can be uploaded',
      },
      selectionVariant: {
        'ui:title': 'Selection Type',
        'ui:description': 'Controls the number of files that can be uploaded',
        'ui:widget': 'ToggleButtonGroupWidget',
        'ua:options': {
          labelVariant: 'text-xs',
        },
      },
      maxFiles: {
        'ui:title': 'Maximum Files',
        'ui:placeholder': 'No limit',
        'ui:description': 'The maximum number of files that can be uploaded',
      },
      maxFileSize: {
        'ui:title': 'Max File Size',
        'ui:placeholder': 'No limit',
        'ui:description': 'The maximum file size (in MB) that can be uploaded per file.',
        'ui:widget': 'MaxFileSizeWidget',
        'ua:options': {
          endDecoratorText: 'MB',
          multiline: false,
        },
      },
      'ui:order': ['supportedFileTypes', 'selectionVariant', 'maxFiles', 'maxFileSize'],
    },
    accessOptions: {
      'ui:title': 'Access',
      'ui:ObjectFieldTemplate': 'InterfaceSectionTemplate',
      'ua:options': {
        disableBackground: true,
        interfaceSection: {
          showTopDivider: true,
          showBottomDivider: true,
          initiallyExpand: true,
          rootClassName: '!-mt-lg',
        },
      },
      fileVisibility: {
        'ui:title': 'File Visibility',
        // default select, not a toggle group: four options do not fit the panel width,
        // and only the select renders each option's caption
        'ui:description': 'Controls who can view uploaded files after submission.',
        'ua:options': {
          labelVariant: 'text-xs',
        },
      },
      publicAccessWarning: {
        'ui:label': false,
        'ui:classNames': '-mt-sm',
        'ui:widget': isFileVisibilityPublic ? 'AlertWidget' : 'hidden',
        'ua:options': {
          color: 'warning',
          icon: 'AlertTriangle',
          description: 'Files accessible to anyone — no authentication required.',
          slotProps: {
            description: {
              variant: 'text-xxs',
              color: 'text-warning',
            },
          },
        },
      },
      allowDownload: {
        'ui:widget': 'ToggleWidget',
        'ui:fieldLayout': [['title', 'children']],
        'ua:options': {
          orientation: 'column',
        },
      },
    },
    divider: { 'ui:label': false, 'ui:widget': 'DividerWidget' },
    divider1: { 'ui:label': false, 'ui:widget': 'DividerWidget' },
    divider2: { 'ui:label': false, 'ui:widget': 'DividerWidget' },
    divider3: { 'ui:label': false, 'ui:widget': 'DividerWidget' },
    interactions: {
      'ui:title': 'Interactions',
      'ui:field': 'InteractionsField',
      'ua:options': {
        events: [{ eventType: EventTypeEnum.OnFileUpload, label: 'On File Uploaded' }],
      },
    },
    permissions: {
      'ui:field': 'BlockPermissionsField',
      'ui:title': 'Permissions',
    },
    addOns: {
      'ui:title': 'Add-ons',
      'ui:field': 'AddOnsField',
      'ua:i18n': true,
      'ua:options': {
        'ua:i18n': true,
        addOns: [
          {
            id: 'description',
            label: 'Description',
            propertyView: 'POPOVER_FORM',
            'ua:i18n': true,
            viewDetails: {
              title: 'Description',
              propertyPath: 'component.content.addOns.description',
              schema: {
                type: 'object',
                properties: {
                  content: {
                    type: 'string',
                  },
                },
              },
              uiSchema: {
                'ua:i18n': true,
                content: {
                  'ui:widget': 'TextareaWidget',
                  'ua:i18n': true,
                },
                'ui:order': ['content'],
              },
            },
            initialValue: {
              content: '',
            },
          },
          {
            id: 'templateFile',
            label: 'Template File',
            showNestedDetails: true,
            'ua:i18n': true,
            viewDetails: {
              viewType: 'FILE_UPLOAD_TEMPLATE_DETAILS',
              propertyPath: 'component.content.addOns.templateFile',
            },
            initialValue: {
              label: 'Template File',
              icon: 'SvgDownload01',
              source: 'url',
              url: '',
            },
          },
        ],
      },
    },
    'ui:order': [
      'label',
      'fileRestrictions',
      'accessOptions',
      'addOns',
      'divider',
      'interactions',
      'divider1',
      'permissions',
      'divider2',
    ],
  };
}
